import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button, Stack, Typography } from '@mui/material';
import { LANDING_PAGE_BLOCK_MIN_HEIGHT_MAX } from '@provision/landing-page-utils';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { BlockBackgroundFields } from 'src/pages/Settings/memberships/components/landing-page-tab/block-fields/block-background-fields';
import { ClosingCtaBlockFields } from 'src/pages/Settings/memberships/components/landing-page-tab/block-fields/closing-cta-block-fields';
import { ContentBlockFields } from 'src/pages/Settings/memberships/components/landing-page-tab/block-fields/content-block-fields';
import { HeroBlockFields } from 'src/pages/Settings/memberships/components/landing-page-tab/block-fields/hero-block-fields';
import { PlansBlockFields } from 'src/pages/Settings/memberships/components/landing-page-tab/block-fields/plans-block-fields';
import { PixelSliderField } from 'src/pages/Settings/memberships/components/landing-page-tab/pixel-slider-field';
import {
  blockTypeIconSx,
  removeBlockButtonSx,
  SyncedChip,
} from 'src/pages/Settings/memberships/components/landing-page-tab/styled';
import {
  BLOCK_SYNCED_NOTES,
  BLOCK_TYPE_ICONS,
  blockFieldName,
  type LandingPageBlockType,
} from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import { AccordionSummaryActionIconButton } from 'src/pages/Settings/memberships/components/plans-tab/accordion-summary-action-icon-button';
import {
  PlanAccordionDetails,
  PlanAccordionSummary,
  PlanAccordionWrapper,
  PlanName,
} from 'src/pages/Settings/memberships/components/plans-tab/styled';
import { useBlockTitle } from 'src/pages/Settings/memberships/hooks/use-block-title';

interface BlockAccordionProps {
  index: number;
  blockId: string;
  type: LandingPageBlockType;
  expanded: boolean;
  onExpandedChange: (blockId: string, expanded: boolean) => void;
  onRemove: (index: number, blockId: string) => void;
  onMove: (from: number, to: number) => void;
  moveUpToIndex: number | null;
  moveDownToIndex: number | null;
  readOnly: boolean;
}

export const BlockAccordion = ({
  index,
  blockId,
  type,
  expanded,
  onExpandedChange,
  onRemove,
  onMove,
  moveUpToIndex,
  moveDownToIndex,
  readOnly,
}: BlockAccordionProps) => {
  const title = useBlockTitle(index, type);
  const syncedNote = BLOCK_SYNCED_NOTES[type];

  return (
    <PlanAccordionWrapper
      elevation={0}
      disableGutters
      expanded={expanded}
      onChange={(_, next) => onExpandedChange(blockId, next)}
      slotProps={{ transition: { mountOnEnter: true, unmountOnExit: true } }}
      data-testid={`landing-page-block-${blockId}`}
    >
      <PlanAccordionSummary expandIcon={<ChevronRightIcon />}>
        <DynamicIcon name={BLOCK_TYPE_ICONS[type]} sx={blockTypeIconSx} />
        <Tooltip title={title}>
          <PlanName variant="body2">{title}</PlanName>
        </Tooltip>
        {syncedNote ? <SyncedChip label="Synced" /> : null}
        {!readOnly ? (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <AccordionSummaryActionIconButton
              aria-label="Move block up"
              disabled={moveUpToIndex === null}
              onAction={() => moveUpToIndex !== null && onMove(index, moveUpToIndex)}
            >
              <ArrowUpwardIcon fontSize="inherit" />
            </AccordionSummaryActionIconButton>
            <AccordionSummaryActionIconButton
              aria-label="Move block down"
              disabled={moveDownToIndex === null}
              onAction={() => moveDownToIndex !== null && onMove(index, moveDownToIndex)}
            >
              <ArrowDownwardIcon fontSize="inherit" />
            </AccordionSummaryActionIconButton>
          </Stack>
        ) : null}
      </PlanAccordionSummary>

      <PlanAccordionDetails>
        <Stack gap={2.5}>
          {syncedNote ? (
            <Typography variant="body2" color="text.secondary">
              {syncedNote}
            </Typography>
          ) : null}

          {type === 'hero' ? <HeroBlockFields index={index} readOnly={readOnly} /> : null}
          {type === 'content' ? (
            <ContentBlockFields index={index} readOnly={readOnly} />
          ) : null}
          {type === 'plans' ? <PlansBlockFields index={index} readOnly={readOnly} /> : null}
          {type === 'closingCta' ? (
            <ClosingCtaBlockFields index={index} readOnly={readOnly} />
          ) : null}
          <BlockBackgroundFields index={index} readOnly={readOnly} />
          <PixelSliderField
            name={blockFieldName(index, 'minHeight')}
            label="Block height"
            min={0}
            max={LANDING_PAGE_BLOCK_MIN_HEIGHT_MAX}
            minLabel="Auto"
            readOnly={readOnly}
          />
        </Stack>

        {!readOnly ? (
          <Button
            startIcon={<DeleteOutlineIcon />}
            variant="text"
            color="error"
            onClick={() => onRemove(index, blockId)}
            sx={removeBlockButtonSx}
          >
            Remove block
          </Button>
        ) : null}
      </PlanAccordionDetails>
    </PlanAccordionWrapper>
  );
};
